import type { MockContext } from '@mocks/mockFetch';
import type { ConnectionChoice } from '@app/types/sdk';

type TokenResponse = {
  accessToken: string;
  refreshToken?: string;
  tokenType: string;
  expiresIn: number;
  scope?: string;
  issuedAt: string;
};

interface DiscoverVerifyPayload {
  email: string;
  code: string;
}

interface PasskeyAssertOptionsPayload {
  emailTx?: string;
  usernameless?: boolean;
}

interface PasskeyVerifyPayload {
  challengeToken: string;
}

interface PasskeyAttestOptionsPayload {
  emailTx: string;
}

interface PasskeyAttestVerifyPayload {
  challengeToken: string;
}

interface PasswordVerifyPayload {
  emailTx?: string;
  email?: string;
  password?: string;
}

interface PasswordRegisterPayload {
  emailTx?: string;
  password?: string;
  newPassword?: string;
}

type MembershipScenario = 'existing' | 'new' | 'passwordSetup';

type MembershipRecord = {
  id: string;
  orgId: string;
  orgName: string;
  rolesResolved?: Array<{ id: string; slug: string; name: string }>;
  status: string;
  connections?: ConnectionChoice[];
  policy?: { requiresSso: boolean };
  note?: string;
};

const buildMockSsoStartUrl = (
  mode: 'oidc' | 'saml' | 'saml-idp',
  params: Record<string, string | undefined> = {}
): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (typeof value === 'string' && value.trim().length > 0) {
      search.set(key, value.trim());
    }
  }
  const query = search.toString();
  return `/mock/sso/${mode}${query ? `?${query}` : ''}`;
};

const INITIAL_MEMBERSHIPS: MembershipRecord[] = [
  {
    id: 'aaaa1111-2222-4333-8444-555555555555',
    orgId: '11111111-1111-4111-8111-111111111111',
    orgName: 'Arcadia Networks',
    rolesResolved: [{ id: 'role-admin', slug: 'admin', name: 'Administrator' }],
    status: 'active',
    connections: [
      {
        type: 'oidc',
        name: 'Arcadia Entra ID',
        startUrl: buildMockSsoStartUrl('oidc', {
          orgId: '11111111-1111-4111-8111-111111111111',
          connection: 'arcadia-entra-oidc'
        })
      },
      {
        type: 'saml',
        name: 'Arcadia Okta (SP)',
        startUrl: buildMockSsoStartUrl('saml', {
          orgId: '11111111-1111-4111-8111-111111111111',
          connection: 'arcadia-okta-sp'
        })
      },
      {
        type: 'saml',
        name: 'Arcadia Okta (IdP)',
        startUrl: buildMockSsoStartUrl('saml-idp', {
          orgId: '11111111-1111-4111-8111-111111111111',
          connection: 'arcadia-okta-idp'
        }),
        idpInitiated: true
      }
    ]
  },
  {
    id: 'bbbb2222-3333-4444-8555-666666666666',
    orgId: '22222222-2222-4222-8222-222222222222',
    orgName: 'Blue Networks',
    rolesResolved: [{ id: 'role-viewer', slug: 'viewer', name: 'Viewer' }],
    status: 'active',
    connections: []
  }
];

const cloneMemberships = (): MembershipRecord[] =>
  INITIAL_MEMBERSHIPS.map((membership) => ({
    ...membership,
    rolesResolved: membership.rolesResolved
      ? membership.rolesResolved.map((role) => ({ ...role }))
      : undefined,
    connections: membership.connections ? membership.connections.map((connection) => ({ ...connection })) : []
  }));

let sessionMemberships: MembershipRecord[] = cloneMemberships();
let activeOrgId: string | null = sessionMemberships[0]?.orgId ?? null;
let sessionScenario: MembershipScenario = 'existing';

function buildJsonResponse(body: unknown, init: ResponseInit = {}): Response {
  return new Response(JSON.stringify(body), {
    status: 200,
    headers: {
      'Content-Type': 'application/json',
      'X-Mock-Route': 'auth'
    },
    ...init
  });
}

async function parseJson<T>(request: Request): Promise<T | null> {
  try {
    return (await request.clone().json()) as T;
  } catch (error) {
    console.warn('[mock:auth] failed to parse request body', error);
    return null;
  }
}

function deriveScenario(email: string): 'existing' | 'new' | 'passwordSetup' {
  const normalized = email.toLowerCase();
  if (
    normalized.includes('+new') ||
    normalized.includes('newuser') ||
    normalized.includes('new-org') ||
    normalized.includes('register')
  ) {
    return 'new';
  }
  if (normalized.includes('setup') || normalized.includes('password')) {
    return 'passwordSetup';
  }
  return 'existing';
}

export const authHandlers = [
  async ({ request, url }: MockContext) => {
    if (request.method === 'GET' && url.pathname.endsWith('/auth/me')) {
      const authorization = request.headers.get('Authorization');
      if (!authorization || authorization.trim().length === 0) {
        return new Response(JSON.stringify({ detail: 'Unauthorized' }), {
          status: 401,
          headers: {
            'Content-Type': 'application/json',
            'X-Mock-Route': 'auth'
          }
        });
      }

      const activeMembership = activeOrgId
        ? sessionMemberships.find((membership) => membership.orgId === activeOrgId)
        : null;
      return buildJsonResponse({
        user: {
          id: '99999999-9999-4999-8999-999999999999',
          email: 'operator@example.com',
          name: 'Mock Operator',
          displayName: 'Mock Operator'
        },
        memberships: sessionMemberships,
        activeOrg: activeMembership
          ? {
              orgId: activeMembership.orgId,
              rolesResolved: activeMembership.rolesResolved ?? []
            }
          : null
      });
    }
    return null;
  },
  async ({ request, url }: MockContext) => {
    if (request.method !== 'POST' || !url.pathname.endsWith('/auth/discover/start')) {
      return null;
    }

    const payload = await parseJson<{ email: string }>(request);
    const email = payload?.email ?? 'operator@example.com';
    const normalized = email.toLowerCase();

    if (normalized.includes('sso-redirect')) {
      return buildJsonResponse({
        nextStep: 'sso_redirect',
        sso: {
          orgId: INITIAL_MEMBERSHIPS[0].orgId,
          orgName: INITIAL_MEMBERSHIPS[0].orgName,
          connection: INITIAL_MEMBERSHIPS[0].connections?.[0] ?? {
            type: 'oidc',
            name: 'Default OIDC',
            startUrl: buildMockSsoStartUrl('oidc', {
              orgId: INITIAL_MEMBERSHIPS[0].orgId,
              connection: 'default-oidc'
            })
          }
        }
      });
    }

    if (normalized.includes('sso-idp')) {
      const idpConnection =
        INITIAL_MEMBERSHIPS[0].connections?.find((connection) => connection.name.includes('IdP')) ?? {
          type: 'saml',
          name: 'Mock IdP',
          startUrl: buildMockSsoStartUrl('saml-idp', {
            orgId: INITIAL_MEMBERSHIPS[0].orgId,
            connection: 'arcadia-okta-idp'
          }),
          idpInitiated: true
        };

      return buildJsonResponse({
        nextStep: 'sso_redirect',
        sso: {
          orgId: INITIAL_MEMBERSHIPS[0].orgId,
          orgName: INITIAL_MEMBERSHIPS[0].orgName,
          connection: idpConnection
        }
      });
    }

    if (normalized.includes('sso-picker') || normalized.includes('existing')) {
      return buildJsonResponse({
        nextStep: 'picker',
        choices: INITIAL_MEMBERSHIPS.map((membership) => ({
          orgId: membership.orgId,
          orgName: membership.orgName,
          connection:
            membership.connections && membership.connections.length > 0
              ? membership.connections[0]
              : {
                  type: 'oidc',
                  name: `${membership.orgName} OIDC`,
                  startUrl: buildMockSsoStartUrl('oidc', {
                    orgId: membership.orgId,
                    connection: `${membership.orgId}-oidc`
                  })
                }
        }))
      });
    }

    if (normalized.includes('sso-verify') || normalized.includes('verify')) {
      return buildJsonResponse({
        nextStep: 'verify_email'
      });
    }

    const decisionType = normalized.match(/sso-flow:(\w+)/)?.[1];
    if (decisionType === 'redirect') {
      return buildJsonResponse({
        nextStep: 'sso_redirect',
        sso: {
          orgId: INITIAL_MEMBERSHIPS[0].orgId,
          orgName: INITIAL_MEMBERSHIPS[0].orgName,
          connection: INITIAL_MEMBERSHIPS[0].connections?.[0] ?? {
            type: 'saml',
            name: 'SAML IdP',
            startUrl: buildMockSsoStartUrl('saml', {
              orgId: INITIAL_MEMBERSHIPS[0].orgId,
              connection: 'arcadia-saml'
            })
          }
        }
      });
    }

    if (decisionType === 'verify') {
      return buildJsonResponse({
        nextStep: 'verify_email'
      });
    }

    return buildJsonResponse({
      nextStep: 'picker',
      choices: INITIAL_MEMBERSHIPS.map((membership) => ({
        orgId: membership.orgId,
        orgName: membership.orgName,
        connection:
          membership.connections && membership.connections.length > 0
            ? membership.connections[0]
            : {
                type: 'oidc',
                name: `${membership.orgName} OIDC`,
                startUrl: buildMockSsoStartUrl('oidc', {
                  orgId: membership.orgId,
                  connection: `${membership.orgId}-oidc`
                })
              }
      }))
    });
  },
  async ({ request, url }: MockContext) => {
    if (request.method !== 'POST' || !url.pathname.endsWith('/auth/invitations/accept')) {
      return null;
    }

    const payload = await parseJson<{ inviteToken?: string }>(request);
    const inviteTokenValue =
      typeof payload?.inviteToken === 'string' ? payload.inviteToken.trim() : '';

    if (!inviteTokenValue) {
      return new Response(JSON.stringify({ detail: 'Missing invite token' }), {
        status: 400,
        headers: {
          'Content-Type': 'application/json',
          'X-Mock-Route': 'auth'
        }
      });
    }

    const normalized = inviteTokenValue.toLowerCase();
    const membershipStatus = normalized.includes('active') ? 'active' : 'pending';
    const requiresSso = normalized.includes('sso-only');
    const disableLocal = requiresSso || normalized.includes('nolocal');
    const includePassword = normalized.includes('password');
    const includeSso = !normalized.includes('nosso');
    const idpOnly = normalized.includes('idponly');

    const localConnections: Array<'passkey' | 'password'> = [];
    if (!disableLocal) {
      localConnections.push('passkey');
      if (includePassword) {
        localConnections.push('password');
      }
    }

    const ssoConnections = includeSso
      ? [
          {
            type: 'oidc',
            name: 'Arcadia OIDC',
            startUrl: idpOnly ? null : buildMockSsoStartUrl('oidc', { orgId: '11111111-1111-4111-8111-111111111111', connection: 'arcadia-oidc' }),
            idpInitiated: idpOnly ? true : undefined
          }
        ]
      : [];

    return buildJsonResponse({
      userId: '99999999-9999-4999-8999-999999999999',
      orgId: '11111111-1111-4111-8111-111111111111',
      membershipStatus,
      emailTx: `etx_mock_invite_${Date.now()}`,
      emailTxExpiresAt: new Date(Date.now() + 10 * 60 * 1000).toISOString(),
      localConnections,
      membership: {
        orgId: '11111111-1111-4111-8111-111111111111',
        orgSlug: 'arcadia-networks',
        orgName: 'Arcadia Networks',
        status: membershipStatus === 'active' ? 'active' : 'pending',
        policy: {
          requiresSso
        },
        connections: ssoConnections
      }
    });
  },
  async ({ request, url }: MockContext) => {
    if (request.method !== 'POST' || !url.pathname.endsWith('/auth/discover/verify')) {
      return null;
    }

    const payload = await parseJson<DiscoverVerifyPayload>(request);
    const email = payload?.email ?? 'operator@example.com';
    const scenarioOverride = email.includes('scenario:')
      ? (email.match(/scenario:(\w+)/)?.[1] ?? '').toLowerCase()
      : null;
    const scenario = scenarioOverride === 'new' || scenarioOverride === 'passwordsetup'
      ? (scenarioOverride as 'new' | 'passwordSetup')
      : deriveScenario(email);
    const { localConnections, memberships } = (() => {
      if (scenario === 'new') {
        return {
          localConnections: ['passkey', 'password'] as Array<'passkey' | 'password'>,
          memberships: []
        };
      }

      if (scenario === 'passwordSetup') {
        return {
          localConnections: ['password'] as Array<'passkey' | 'password'>,
          memberships: [
            {
              orgId: '33333333-3333-4333-8333-333333333333',
              orgName: 'Password Setup Org',
              status: 'active',
              policy: {
                requiresSso: false
              },
              note: 'Finish setting your password to continue.'
            }
          ]
        };
      }

      const connections: ConnectionChoice[] = [
        {
          type: 'oidc',
          name: 'Arcadia Entra ID',
          startUrl: buildMockSsoStartUrl('oidc', {
            orgId: '11111111-1111-4111-8111-111111111111',
            connection: 'arcadia-entra-oidc'
          })
        },
        {
          type: 'saml',
          name: 'Arcadia Okta (SP)',
          startUrl: buildMockSsoStartUrl('saml', {
            orgId: '11111111-1111-4111-8111-111111111111',
            connection: 'arcadia-okta-sp'
          })
        },
        {
          type: 'saml',
          name: 'Arcadia Okta (IdP)',
          startUrl: buildMockSsoStartUrl('saml-idp', {
            orgId: '11111111-1111-4111-8111-111111111111',
            connection: 'arcadia-okta-idp'
          }),
          idpInitiated: true
        }
      ];

      return {
        localConnections: ['passkey', 'password'] as Array<'passkey' | 'password'>,
        memberships: [
          {
            orgId: '11111111-1111-4111-8111-111111111111',
            orgName: 'Arcadia Networks',
            status: 'active',
            connections,
            policy: {
              requiresSso: false
            }
          },
          {
            orgId: '22222222-2222-4222-8222-222222222222',
            orgName: 'Blue Networks',
            status: 'active',
            connections: [],
            policy: {
              requiresSso: false
            }
          },
          {
            orgId: '44444444-4444-4444-4444-444444444444',
            orgName: 'Fanta',
            status: 'active',
            connections: [],
            policy: {
              requiresSso: true
            },
            note: 'This organization requires SSO. Ask an admin to configure a connector.'
          },
          {
            orgId: '55555555-5555-5555-5555-555555555555',
            orgName: 'Invite Pending Org',
            status: 'invited',
            note: 'Use your invitation email to finish joining this org.'
          }
        ]
      };
    })();

    sessionScenario = scenario;
    if (scenario === 'new') {
      sessionMemberships = [];
      activeOrgId = null;
    } else if (scenario === 'passwordSetup') {
      sessionMemberships = [
        {
          id: 'pset-33333333-3333-4333-8333-333333333333',
          orgId: '33333333-3333-4333-8333-333333333333',
          orgName: 'Password Setup Org',
          status: 'active',
          rolesResolved: [{ id: 'role-viewer', slug: 'viewer', name: 'Viewer' }],
          connections: [],
          policy: {
            requiresSso: false
          },
          note: 'Finish setting your password to continue.'
        }
      ];
      activeOrgId = sessionMemberships[0].orgId;
    } else {
      sessionMemberships = cloneMemberships();
      activeOrgId = sessionMemberships[0]?.orgId ?? null;
    }

    return buildJsonResponse({
      email,
      emailTx: 'etx_mock_handle',
      emailTxExpiresAt: new Date(Date.now() + 5 * 60 * 1000).toISOString(),
      localConnections,
      memberships
    });
  },
  async ({ request, url }: MockContext) => {
    if (request.method !== 'POST' || !url.pathname.endsWith('/auth/password/verify')) {
      return null;
    }

    const payload = await parseJson<PasswordVerifyPayload>(request);
    const password = payload?.password ?? '';
    const email = payload?.email ?? '';

    if ((!payload?.emailTx && !email) || password.length === 0) {
      return new Response(JSON.stringify({ detail: 'Missing credentials' }), {
        status: 400,
        headers: {
          'Content-Type': 'application/json',
          'X-Mock-Route': 'auth'
        }
      });
    }

    if (password.trim().length < 4) {
      return new Response(JSON.stringify({ detail: 'Incorrect password supplied.' }), {
        status: 401,
        headers: {
          'Content-Type': 'application/json',
          'X-Mock-Route': 'auth'
        }
      });
    }

    const multiOrg = email.toLowerCase().includes('multi');
    const loginTx = multiOrg ? 'ltx_requires_org' : 'ltx_password_mock';

    return buildJsonResponse({
      loginTx,
      loginTxExpiresAt: new Date(Date.now() + 5 * 60 * 1000).toISOString()
    });
  },
  async ({ request, url }: MockContext) => {
    if (request.method !== 'POST' || !url.pathname.endsWith('/auth/password/register')) {
      return null;
    }

    const payload = await parseJson<PasswordRegisterPayload>(request);
    const newPassword = payload?.newPassword ?? payload?.password;
    if (!payload?.emailTx || !newPassword) {
      return new Response(JSON.stringify({ detail: 'Missing emailTx or password' }), {
        status: 400,
        headers: {
          'Content-Type': 'application/json',
          'X-Mock-Route': 'auth'
        }
      });
    }

    if (newPassword.length < 12) {
      return new Response(JSON.stringify({ detail: 'Password does not meet minimum requirements.' }), {
        status: 422,
        headers: {
          'Content-Type': 'application/json',
          'X-Mock-Route': 'auth'
        }
      });
    }

    const issueLoginTx = url.searchParams.get('issueLoginTx') === 'true';

    return buildJsonResponse({
      registeredAt: new Date().toISOString(),
      loginTx: issueLoginTx ? 'ltx_password_mock' : undefined,
      loginTxExpiresAt: issueLoginTx ? new Date(Date.now() + 5 * 60 * 1000).toISOString() : undefined
    });
  },
  async ({ request, url }: MockContext) => {
    if (request.method !== 'POST' || !url.pathname.endsWith('/auth/password/reset')) {
      return null;
    }

    const payload = await parseJson<PasswordRegisterPayload>(request);
    const newPassword = payload?.newPassword ?? payload?.password;
    if (!payload?.emailTx || !newPassword) {
      return new Response(JSON.stringify({ detail: 'Missing emailTx or password' }), {
        status: 400,
        headers: {
          'Content-Type': 'application/json',
          'X-Mock-Route': 'auth'
        }
      });
    }

    if (newPassword.length < 12) {
      return new Response(JSON.stringify({ detail: 'Password does not meet minimum requirements.' }), {
        status: 422,
        headers: {
          'Content-Type': 'application/json',
          'X-Mock-Route': 'auth'
        }
      });
    }

    const issueLoginTx = url.searchParams.get('issueLoginTx') === 'true';

    return buildJsonResponse({
      registeredAt: new Date().toISOString(),
      loginTx: issueLoginTx ? 'ltx_password_mock' : undefined,
      loginTxExpiresAt: issueLoginTx ? new Date(Date.now() + 5 * 60 * 1000).toISOString() : undefined
    });
  },
  async ({ request, url }: MockContext) => {
    if (request.method !== 'POST' || !url.pathname.endsWith('/auth/passkeys/assert')) {
      return null;
    }

    const payload = await parseJson<PasskeyAssertOptionsPayload>(request);
    const rpId =
      typeof window !== 'undefined' && window.location.hostname
        ? window.location.hostname
        : 'portal.reapitnow.ai';

    return buildJsonResponse({
      publicKey: {
        challenge: 'bW9ja19wYXNza2V5X2NoYWxsZW5nZQ',
        rpId,
        timeout: 60000,
        userVerification: payload?.usernameless ? 'required' : 'preferred',
        allowCredentials: payload?.usernameless
          ? []
          : [
              {
                type: 'public-key',
                id: 'bW9ja19jcmVkZW50aWFsX2lk',
                transports: ['internal']
              }
            ]
      },
      challengeToken: 'ct_mock_token',
      challengeTokenExpiresAt: new Date(Date.now() + 2 * 60 * 1000).toISOString()
    });
  },
  async ({ request, url }: MockContext) => {
    if (request.method !== 'POST' || !url.pathname.endsWith('/auth/passkeys/assert/verify')) {
      return null;
    }

    const payload = await parseJson<PasskeyVerifyPayload>(request);
    if (!payload?.challengeToken) {
      return new Response(JSON.stringify({ message: 'Missing challenge token' }), {
        status: 400,
        headers: {
          'Content-Type': 'application/json',
          'X-Mock-Route': 'auth'
        }
      });
    }

    return buildJsonResponse({
      loginTx: 'ltx_mock_handle',
      loginTxExpiresAt: new Date(Date.now() + 5 * 60 * 1000).toISOString()
    });
  },
  async ({ request, url }: MockContext) => {
    if (request.method !== 'POST' || !url.pathname.endsWith('/auth/passkeys/attest')) {
      return null;
    }

    const payload = await parseJson<PasskeyAttestOptionsPayload>(request);
    if (!payload?.emailTx) {
      return new Response(JSON.stringify({ message: 'Missing emailTx handle' }), {
        status: 400,
        headers: {
          'Content-Type': 'application/json',
          'X-Mock-Route': 'auth'
        }
      });
    }

    const rpId =
      typeof window !== 'undefined' && window.location.hostname
        ? window.location.hostname
        : 'portal.reapitnow.ai';

    return buildJsonResponse({
      publicKey: {
        challenge: 'YXR0ZXN0X21vY2tfY2hhbGxlbmdl',
        rp: {
          name: 'ReapITNow',
          id: rpId
        },
        user: {
          id: 'dXNlcl9tb2NrX2lk',
          name: 'operator@example.com',
          displayName: 'Mock Operator'
        },
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },
          { type: 'public-key', alg: -257 }
        ],
        timeout: 60000,
        authenticatorSelection: {
          userVerification: 'preferred'
        }
      },
      challengeToken: 'ct_mock_attest',
      challengeTokenExpiresAt: new Date(Date.now() + 2 * 60 * 1000).toISOString()
    });
  },
  async ({ request, url }: MockContext) => {
    if (request.method !== 'POST' || !url.pathname.endsWith('/auth/passkeys/attest/verify')) {
      return null;
    }

    const payload = await parseJson<PasskeyAttestVerifyPayload>(request);
    if (!payload?.challengeToken) {
      return new Response(JSON.stringify({ message: 'Missing challenge token' }), {
        status: 400,
        headers: {
          'Content-Type': 'application/json',
          'X-Mock-Route': 'auth'
        }
      });
    }

    return buildJsonResponse({
      credentialId: 'mock-credential-id',
      registeredAt: new Date().toISOString(),
      nextStep: 'exchangeLoginTx',
      loginTx: 'ltx_mock_handle',
      loginTxExpiresAt: new Date(Date.now() + 5 * 60 * 1000).toISOString()
    });
  },
  async ({ request, url }: MockContext) => {
    if (request.method !== 'POST' || !url.pathname.endsWith('/auth/token')) {
      return null;
    }

    const payload = await parseJson<Record<string, any>>(request);
    if (!payload) {
      return new Response(JSON.stringify({ detail: 'Invalid token request' }), {
        status: 400,
        headers: {
          'Content-Type': 'application/json',
          'X-Mock-Route': 'auth'
        }
      });
    }

    const { grantType } = payload;

    if (grantType === 'loginTx' && payload.loginTx === 'ltx_requires_org' && !payload.orgId) {
      const selectionMemberships =
        sessionMemberships.length > 0 ? sessionMemberships : cloneMemberships();

      return buildJsonResponse({
        nextStep: 'pickOrg',
        memberships: selectionMemberships.map((membership) => ({
          orgId: membership.orgId,
          orgName: membership.orgName
        }))
      });
    }

    if (grantType === 'loginTx') {
      if (sessionScenario === 'new') {
        sessionMemberships = [];
        activeOrgId = payload.orgId ?? null;
      } else if (sessionMemberships.length === 0) {
        sessionMemberships = cloneMemberships();
        activeOrgId = sessionMemberships[0]?.orgId ?? null;
      }

      if (payload.orgId) {
        activeOrgId = payload.orgId;
      }
    }

    if (grantType === 'refresh') {
      if (!payload.refreshToken) {
        return new Response(JSON.stringify({ detail: 'Missing refresh token' }), {
          status: 400,
          headers: {
            'Content-Type': 'application/json',
            'X-Mock-Route': 'auth'
          }
        });
      }
      if (payload.orgId) {
        activeOrgId = payload.orgId;

        if (!sessionMemberships.some((membership) => membership.orgId === payload.orgId)) {
          const globalState = globalThis as Record<string, any>;
          const lastCreatedOrg = globalState.__mockLastCreatedOrg;
          const orgName =
            lastCreatedOrg && lastCreatedOrg.id === payload.orgId
              ? lastCreatedOrg.name
              : `Organization ${payload.orgId.slice(0, 6)}`;

          sessionMemberships = [
            ...sessionMemberships,
            {
              id: payload.orgId,
              orgId: payload.orgId,
              orgName: orgName ?? payload.orgId,
              status: 'active',
              rolesResolved: [{ id: 'role-admin', slug: 'admin', name: 'Administrator' }],
              connections: []
            }
          ];

          if (lastCreatedOrg && lastCreatedOrg.id === payload.orgId) {
            delete globalState.__mockLastCreatedOrg;
          }
        }

        sessionScenario = 'existing';
      }
    }

    const token: TokenResponse = {
      accessToken: `mock-access-token-${activeOrgId ? activeOrgId.slice(0, 6) : 'preorg'}-${Date.now()}`,
      refreshToken: 'mock-refresh-token-xyz789',
      tokenType: 'Bearer',
      expiresIn: 3600,
      scope: activeOrgId ? `org:${activeOrgId}` : 'bootstrap',
      issuedAt: new Date().toISOString()
    };

    return buildJsonResponse(token);
  }
];
